import { useEffect, useRef, type RefObject } from 'react';

/**
 * Escape closes a hand-rolled dropdown/popover panel and puts focus back on its trigger
 * (the first button in the container). It marks the event handled, so the global Escape
 * "go back" shortcut doesn't also navigate the page away (see useKeyboardShortcuts).
 */
export function useEscapeToClose(open: boolean, close: () => void, containerRef: RefObject<HTMLElement | null>) {
  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  }, [close]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      closeRef.current();
      containerRef.current?.querySelector<HTMLElement>('button')?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, containerRef]);
}
